import { Component, type ReactNode } from 'react';
import { Button } from './controls.tsx';
import { Screen } from './screen.tsx';
import type { ScreenKind } from './scope.ts';
import { strings } from './strings.ts';
import { APP_VERSION } from './version.ts';

const RELOADED = 'makan:reloaded-for';

/**
 * A page's file that will not load is most often one a deploy replaced while
 * the tab was open: the tab still runs the old build, whose chunks the new
 * one may no longer hold. Loads the address again, once per address and
 * build (this module's own hashed URL names the build), so the tab fetches
 * the new build; pages keep their state in the URL, so the person lands
 * where they were. Failing again, or with no
 * sessionStorage to remember the try (a reload loop), the error reaches the
 * page boundary.
 */
export function reloadOnFailure<T>(load: () => Promise<T>): () => Promise<T> {
  return () =>
    load().catch((error: unknown) => {
      const tried = `${import.meta.url} ${location.href}`;
      try {
        if (sessionStorage.getItem(RELOADED) === tried) throw error;
        sessionStorage.setItem(RELOADED, tried);
      } catch {
        throw error;
      }
      location.reload();
      return new Promise<T>(() => undefined);
    });
}

/**
 * Around every routed page (app.tsx): a page that throws while it draws, or
 * whose file will not load, shows "Something went wrong" with Retry in the
 * page's language, never a blank page. Retry loads the address again, which
 * also fetches a newer build. The error goes to the console; there is no
 * client error reporting yet.
 */
export class PageBoundary extends Component<{ readonly kind: ScreenKind; readonly children: ReactNode }, { readonly failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { readonly failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    console.error('page failed', APP_VERSION, error);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <Screen kind={this.props.kind} title={strings.common.brokenTitle} lead={strings.common.brokenBody}>
        <Button
          type="button"
          main
          onClick={() => {
            location.reload();
          }}
        >
          {strings.common.retry}
        </Button>
      </Screen>
    );
  }
}

/** Around the features' layers (app.tsx): a layer is an overlay, so one that fails logs and draws nothing, leaving the page under it. */
export class LayerBoundary extends Component<{ readonly children: ReactNode }, { readonly failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { readonly failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    console.error('layer failed', APP_VERSION, error);
  }

  override render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}
